import { Sparkles, Lock } from "lucide-react";
import { useBetaState } from "@/hooks/useBetaState";

/**
 * Sitewide top-bar notice mounted above MarketingNav. Shows live remaining
 * Beta seat count while the launch cap is enabled. Hidden entirely once an
 * admin disables the cap from /admin → Beta.
 *
 * Uses the existing `--primary` token (deep indigo light, light indigo dark)
 * so it respects theme — no hardcoded colors.
 */
export function BetaTopStrip() {
  const { enabled, remaining, cap, remainingKnown, loading } = useBetaState();

  // Once loaded, if an admin has disabled the cap, render nothing at all.
  if (!loading && !enabled) return null;

  // While loading on the very first mount (no cache yet), reserve the strip's
  // vertical space with an invisible placeholder so the page below doesn't
  // jump when the fetch resolves. Subsequent navigations hit the module cache
  // and skip this branch entirely.
  if (loading) {
    return (
      <div
        aria-hidden
        className="w-full border-b border-transparent bg-transparent"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-4 py-1.5 text-xs sm:text-sm">
          <span className="opacity-0">placeholder</span>
        </div>
      </div>
    );
  }

  const closed = remainingKnown && remaining <= 0;
  const Icon = closed ? Lock : Sparkles;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full border-b border-primary/20 bg-primary/10 text-primary"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-4 py-1.5 text-xs sm:text-sm">
        <Icon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
        {closed ? (
          <span className="truncate">
            <span className="hidden sm:inline">Beta is full — signups paused at {cap} accounts.</span>
            <span className="sm:hidden">Beta full · {cap}/{cap}</span>
          </span>
        ) : remainingKnown ? (
          <span className="truncate">
            <span className="hidden sm:inline">
              Launch beta · <strong className="font-semibold">{remaining}</strong> of {cap} seats remaining
            </span>
            <span className="sm:hidden">
              <strong className="font-semibold">{remaining}</strong>/{cap} Beta seats
            </span>
          </span>
        ) : (
          <span className="truncate">
            <span className="hidden sm:inline">
              Launch beta · limited to <strong className="font-semibold">{cap}</strong> accounts
            </span>
            <span className="sm:hidden">
              Launch beta · {cap} seats
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
